import { isNil as _isNil, uniq as _uniq } from 'lodash-es'
import { z } from 'zod'
import type { Option } from '@/components/base/Combobox.vue'
import type { FormContext } from 'vee-validate'
import {
  DEFAULT_PATIENT_FIELD_VISIBILITY,
  PatientFieldName,
  type FieldVisibility,
} from '@/models/patients.js'
import {
  patientFormInsuranceNumberField,
  patientFormPassesIfInsuranceVerification,
  patientInsuranceFormZodSchema,
  type PatientIfInsuranceRegulations,
  type PatientInsuranceInfo,
} from '@/models/patientsInsurance.js'
import { ContentTypeModelName } from '@/models/customFields.js'
import {
  type CountryWithRegionField,
  COUNTRY_REGION_FIELD_COUNTRIES,
} from '@/composables/address.js'
import { fromISOStateCode } from '@/utils/address.js'
import { formatDate } from '@/utils/date/conversion.js'
import { useCustomFieldsForModel } from '@/composables/customFields.js'
import { useCurrentLocaleDateFormat } from '@/composables/i18next.js'
import {
  patientSex,
  type PatientCreate,
  type PatientSex,
  useEnumSexTranslation,
} from '@/composables/patients.js'
import { useSpecies } from '@/composables/species.js'
import { useAuth } from '@/composables/useAuth.js'
import { useFormValidation } from '@/composables/useFormValidation.js'
import useTranslation from '@/composables/useTranslation.js'
import type { PatientCPRStatus } from '@/composables/patientCPRStatus.js'
export type { PatientInsuranceInfo } from '@/models/patientsInsurance.js'

export type PatientBasicInfo = {
  name: string
  officialName?: string
  avatar?: string
  type: string
  subtype?: string[]
  statuses?: string[]
  reasonOfDeath?: string
  dateOfDeath?: string
  cprStatus?: PatientCPRStatus
  dateOfBirth: string
  dateOfRegistration?: string
  notForFood?: boolean
  species: string
  breed: string
  gender?: PatientSex
  bloodGroup?: PatientBloodGroup
  color?: string
  additionalIdentification?: string
  farmStableName?: string
  holdingPlaceNumber?: string | number
  homeDepartment?: string
  remarks?: string
  tags?: string[]
  nonFieldErrors?: string
}

export type PatientIdentificationInfo = PatientIdentificationValues

export type PatientPrivacyInfo = {
  isPrivate?: boolean
  visibleForDepartments?: string[]
  visibleForDepartmentGroups?: string[]
  primaryClinicLocation?: string
}

export type PatientAddressInfo = {
  address?: {
    street_address?: string
    street_address_2?: string
    zip_code?: string
    city?: string
    state?: string
    country_region?: string
    country?: string
  }
}

export interface PatientDetailsFormSchema
  extends
    PatientBasicInfo,
    PatientIdentificationInfo,
    PatientMicrochipValues,
    PatientPrivacyInfo,
    PatientAddressInfo,
    PatientInsuranceInfo {
  customFields?: Record<string, string>
  ignoreDuplicates?: string
}

export type PatientMicrochipValues = {
  microchip?: string
  microchipDate?: string
  microchipPlacement?: string
}

export type PatientIdentificationValues = {
  registerNumber?: string
  animalCode?: string
  tagId?: string
  tagIdDate?: string
  tagIdPlacement?: string
  passport?: string
  passportDate?: string
  earNotched?: string
  earNotchedDate?: string
  earNotchedPlacement?: string
  tattoedId?: string
  tattoedIdDate?: string
  tattoedIdPlacement?: string
  freezeMarking?: string
  freezeMarkingDate?: string
  freezeMarkingPlacement?: string
  hotBrandedId?: string
  hotBrandedIdDate?: string
  hotBrandedIdPlacement?: string
  paintBranding?: string
  paintBrandingDate?: string
  paintBrandingPlacement?: string
}

export const PatientMicrochipFields = {
  Microchip: 'microchip',
  MicrochipDate: 'microchipDate',
  MicrochipPlacement: 'microchipPlacement',
} as const

export const PatientIdentificationFields = {
  RegisterNumber: 'registerNumber',
  AnimalCode: 'animalCode',
  TagId: 'tagId',
  TagIdDate: 'tagIdDate',
  TagIdPlacement: 'tagIdPlacement',
  Passport: 'passport',
  PassportDate: 'passportDate',
  EarNotched: 'earNotched',
  EarNotchedDate: 'earNotchedDate',
  EarNotchedPlacement: 'earNotchedPlacement',
  TattoedId: 'tattoedId',
  TattoedIdDate: 'tattoedIdDate',
  TattoedIdPlacement: 'tattoedIdPlacement',
  FreezeMarking: 'freezeMarking',
  FreezeMarkingDate: 'freezeMarkingDate',
  FreezeMarkingPlacement: 'freezeMarkingPlacement',
  HotBrandedId: 'hotBrandedId',
  HotBrandedIdDate: 'hotBrandedIdDate',
  HotBrandedIdPlacement: 'hotBrandedIdPlacement',
  PaintBranding: 'paintBranding',
  PaintBrandingDate: 'paintBrandingDate',
  PaintBrandingPlacement: 'paintBrandingPlacement',
} as const

export enum PatientIdentifications {
  RegisterNumber = 'registerNumber',
  AnimalCode = 'animalCode',
  Microchip = 'microchip',
  Passport = 'passport',
  TagId = 'tagId',
  EarNotched = 'earNotched',
  TattoedId = 'tattoedId',
  FreezeMarking = 'freezeMarking',
  HotBrandedId = 'hotBrandedId',
  PaintBranding = 'paintBranding',
}

export enum PatientTypes {
  SmallAnimal = 'SMALL_ANIMAL',
  FarmProduction = 'FARM_PRODUCTION',
  Equine = 'EQUINE',
  ExoticOther = 'EXOTIC_OTHER',
}

const PatientSubTypes = {
  NotFoodProductionAnimal: 'NOT_FOOD_PRODUCTION_ANIMAL',
  ZooAnimal: 'ZOO_ANIMAL',
  LabResearchAnimal: 'LAB_RESEARCH_ANIMAL',
} as const

export const PatientStatusValues = {
  Archived: 'ARCHIVED',
  Deceased: 'DECEASED',
  Active: 'ACTIVE',
} as const
export type PatientStatus =
  (typeof PatientStatusValues)[keyof typeof PatientStatusValues]

export const PatientCPRStatusValues = {
  CPR: 'CPR',
  ACPR: 'ACPR',
  DNR: 'DNR',
  NotAsked: 'NOT_ASKED',
} as const satisfies Record<string, PatientCPRStatus>

export const PatientBloodGroupValues = {
  DEA11Negative: 'DEA_1_1_NEGATIVE',
  DEA11Positive: 'DEA_1_1_POSITIVE',
  A: 'A',
  B: 'B',
  AB: 'AB',
  C: 'C',
  D: 'D',
  K: 'K',
  P: 'P',
  Q: 'Q',
  U: 'U',
  T: 'T',
} as const
export type PatientBloodGroup =
  (typeof PatientBloodGroupValues)[keyof typeof PatientBloodGroupValues]

export const AvailablePatientSpecies = [
  'Cat',
  'Dog',
  'Bird',
  'Rabbit',
  'Horse',
] as const
export type PatientSpecies = (typeof AvailablePatientSpecies)[number]

export function usePatientDetailsForm({
  formElement,
  fieldVisibility,
  isFieldRequiredByConfig,
  requiredTagsByConfig,
  getRegulation = () => undefined,
  getInitialValue = () => undefined,
}: {
  formElement: ReturnType<typeof useTemplateRef<HTMLFormElement>>
  fieldVisibility?: MaybeRefOrGetter<FieldVisibility | undefined>
  isFieldRequiredByConfig?: (fieldName: string) => boolean
  requiredTagsByConfig?: MaybeRefOrGetter<Record<string, string> | undefined>
  getRegulation?: (regulationId: string) => unknown
  getInitialValue?: (fieldName: string) => unknown
}) {
  const tPatientDetails = useTranslation('pages.patient.details')
  const tCommonDateTimePicker = useTranslation('common.dateTimePicker')
  const tCommonValidation = useTranslation('common.generalValidations')
  const tPatientIdentification = useTranslation('common.patientIdentification')
  const tPatientInsurance = useTranslation('pages.patient.sections.insurance')

  const optionalRegulationString = (
    value: unknown,
  ): string | null | undefined => {
    if (value === null || value === undefined || typeof value === 'string') {
      return value
    }
    if (typeof value === 'number') {
      return String(value)
    }
    return undefined
  }

  const readIfInsuranceRegs = (): PatientIfInsuranceRegulations => ({
    companyId: optionalRegulationString(
      getRegulation('if_insurance_company_id'),
    ),
    numberValidPattern: optionalRegulationString(
      getRegulation('if_insurance_number_valid_pattern'),
    ),
    verificationCustomFieldId: optionalRegulationString(
      getRegulation('if_insurance_verification_field_id'),
    ),
  })

  const { regexRules } = useFormValidation(formElement)
  const {
    customFields,
    pairedCustomFields,
    validationSchema: customFieldsValidationSchema,
  } = useCustomFieldsForModel(ContentTypeModelName.Patient, 'customFields')

  const isIfInsuranceVerificationFieldRequired = () => {
    const verificationCustomFieldId =
      readIfInsuranceRegs().verificationCustomFieldId

    if (!verificationCustomFieldId) {
      return false
    }

    return customFields.value.some(
      (field) =>
        field.id === verificationCustomFieldId && Boolean(field.required),
    )
  }

  const visibility = computed<FieldVisibility>(
    () => toValue(fieldVisibility) || DEFAULT_PATIENT_FIELD_VISIBILITY,
  )
  const requiredTags = computed(() => toValue(requiredTagsByConfig) ?? {})
  const requiredTagIds = computed(() => Object.keys(requiredTags.value))
  const requiredTagNames = computed(() =>
    Object.values(requiredTags.value).join(', '),
  )

  const checkRequirementWhenVisible = (
    isVisible: boolean | undefined,
    fieldName: string,
  ) =>
    Boolean(
      isVisible &&
      (isFieldRequiredByConfig ? isFieldRequiredByConfig(fieldName) : true),
    )

  function requiredOrOptional(
    isVisible: boolean,
    fieldName: string,
    errorMsg: string,
  ): z.ZodString | z.ZodOptional<z.ZodString>
  function requiredOrOptional<TSchema extends z.ZodTypeAny>(
    isVisible: boolean,
    fieldName: string,
    schema: TSchema,
  ): TSchema | z.ZodOptional<TSchema>
  function requiredOrOptional<
    TRequiredSchema extends z.ZodTypeAny,
    TOptionalSchema extends z.ZodTypeAny,
  >(
    isVisible: boolean,
    fieldName: string,
    requiredSchema: TRequiredSchema,
    optionalSchema: TOptionalSchema,
  ): TRequiredSchema | z.ZodOptional<TOptionalSchema>
  function requiredOrOptional<TSchema extends z.ZodTypeAny>(
    isVisible: boolean,
    fieldName: string,
    errorMsgOrRequiredSchema: string | TSchema,
    schema?: TSchema,
  ) {
    const isRequired = Boolean(
      isVisible && isFieldRequiredByConfig?.(fieldName),
    )

    if (typeof errorMsgOrRequiredSchema === 'string') {
      return isRequired
        ? z.string().min(1, errorMsgOrRequiredSchema)
        : z.string().optional()
    }

    if (schema) {
      return isRequired
        ? errorMsgOrRequiredSchema
        : (schema.optional() as z.ZodOptional<TSchema>)
    }

    return isRequired
      ? errorMsgOrRequiredSchema
      : errorMsgOrRequiredSchema.optional()
  }

  const getTagsSchema = () => {
    const isVisible = Boolean(visibility.value.tags)
    if (!isVisible) {
      return z.array(z.string()).optional()
    }

    const requiredIds = requiredTagIds.value
    if (requiredIds.length > 0) {
      return z
        .array(z.string())
        .refine(
          (tags) => tags.some((tag) => requiredIds.includes(String(tag))),
          {
            message: tPatientDetails('fields.tags.validation.requiredTag', {
              tagNames: requiredTagNames.value,
            }),
          },
        )
    }

    if (checkRequirementWhenVisible(isVisible, PatientFieldName.Tags)) {
      return z
        .array(z.string())
        .min(1, tPatientDetails('fields.tags.validation.required'))
    }

    return z.array(z.string()).optional()
  }

  const initialValues = ref<PatientDetailsFormSchema>({
    name: '',
    officialName: '',
    type: PatientTypes.SmallAnimal,
    subtype: [],
    statuses: [],
    reasonOfDeath: '',
    dateOfDeath: '',
    cprStatus: undefined,
    dateOfBirth: '',
    dateOfRegistration: '',
    notForFood: false,
    species: '',
    breed: '',
    gender: visibility.value.gender
      ? (getInitialValue?.(PatientFieldName.Gender) as PatientSex | undefined)
      : undefined,
    bloodGroup: undefined,
    color: '',
    additionalIdentification: '',
    isPrivate: false,
    visibleForDepartments: [],
    visibleForDepartmentGroups: [],
    primaryClinicLocation: '',
    farmStableName: '',
    holdingPlaceNumber: '',
    insuranceCompany: '',
    insuranceNumber: '',
    insuranceExcessFeeType: undefined,
    insuranceExcessFeeAmount: undefined,
    insurancePolicyEndDate: undefined,
    registerNumber: '',
    animalCode: '',
    microchip: '',
    microchipDate: '',
    microchipPlacement: '',
    passport: '',
    passportDate: '',
    tattoedId: '',
    tattoedIdDate: '',
    tattoedIdPlacement: '',
    freezeMarking: '',
    freezeMarkingDate: '',
    freezeMarkingPlacement: '',
    earNotched: '',
    earNotchedDate: '',
    earNotchedPlacement: '',
    tagId: '',
    tagIdDate: '',
    tagIdPlacement: '',
    hotBrandedId: '',
    hotBrandedIdDate: '',
    hotBrandedIdPlacement: '',
    paintBranding: '',
    paintBrandingDate: '',
    paintBrandingPlacement: '',
    customFields: {},
    homeDepartment: '',
    remarks: '',
    tags: [],
    address: {
      street_address: '',
      street_address_2: '',
      zip_code: '',
      city: '',
      state: '',
      country: '',
    },
  })

  const { handleErrors } = useFormValidation(formElement)
  const {
    values,
    handleSubmit,
    resetForm,
    resetField,
    setValues,
    setFieldValue,
    setErrors,
    errors,
  } = useForm<PatientDetailsFormSchema>({
    validationSchema: computed(() =>
      toTypedSchema(
        z
          .intersection(
            z.object({
              name: z
                .string()
                .min(1, tPatientDetails('fields.name.validation.required')),
              officialName: requiredOrOptional(
                Boolean(visibility.value.officialName),
                PatientFieldName.OfficialName,
                tPatientDetails('fields.officialName.validation.required'),
              ),
              type: requiredOrOptional(
                Boolean(visibility.value.patientTypes),
                PatientFieldName.PatientTypes,
                tPatientDetails('fields.type.validation.required'),
              ),
              subtype: z.array(z.string()).optional(),
              statuses: z.array(z.string()).optional(),
              reasonOfDeath: z.string().optional(),
              dateOfDeath: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODate.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              cprStatus: z.nativeEnum(PatientCPRStatusValues).optional(),
              dateOfBirth: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODate.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              dateOfRegistration: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODate.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              notForFood: z.boolean().optional(),
              species: requiredOrOptional(
                Boolean(visibility.value.species),
                PatientFieldName.Species,
                tPatientDetails('fields.species.validation.required'),
              ),
              breed: z.string().optional(),
              gender: requiredOrOptional(
                Boolean(visibility.value.gender),
                PatientFieldName.Gender,
                z.enum(patientSex).refine((val) => !_isNil(val), {
                  message: tPatientDetails('fields.sex.validation.required'),
                }),
                z.enum(patientSex),
              ),
              bloodGroup: z.preprocess(
                (val) => val || undefined,
                z.nativeEnum(PatientBloodGroupValues).optional(),
              ),
              color: z.string().optional(),
              additionalIdentification: z.string().optional(),
              isPrivate: z.boolean().optional(),
              visibleForDepartments: z.array(z.string()).optional(),
              visibleForDepartmentGroups: z.array(z.string()).optional(),
              primaryClinicLocation: z.string().optional(),
              farmStableName: z.string().optional(),
              holdingPlaceNumber: z.union([z.string(), z.number()]).optional(),
              registerNumber: z.string().optional(),
              animalCode: z.string().optional(),
              microchip: (() => {
                const maxLength = getRegulation?.('microchip_max_length') as
                  | number
                  | undefined
                const base = requiredOrOptional(
                  Boolean(visibility.value.microchip),
                  PatientFieldName.Microchip,
                  tCommonDateTimePicker('required'),
                )
                if (maxLength !== null && typeof maxLength === 'number') {
                  return base.refine(
                    (val) => !val || val.length <= maxLength,
                    tPatientIdentification('validation.microchipMaxLength', {
                      max: maxLength,
                    }),
                  )
                }
                return base
              })(),
              microchipDate: requiredOrOptional(
                Boolean(visibility.value.microchipDate),
                PatientFieldName.MicrochipDate,
                tCommonDateTimePicker('required'),
              ).refine(
                (val) => !val || regexRules.ISODate.test(val),
                tCommonDateTimePicker('invalidFormat'),
              ),
              microchipPlacement: z.string().optional(),
              passport: requiredOrOptional(
                Boolean(visibility.value.passportNumber),
                PatientFieldName.PassportNumber,
                tCommonDateTimePicker('required'),
              ),
              passportDate: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODate.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              tattoedId: z.string().optional(),
              tattoedIdDate: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODateTime.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              tattoedIdPlacement: z.string().optional(),
              freezeMarking: z.string().optional(),
              freezeMarkingDate: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODateTime.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              freezeMarkingPlacement: z.string().optional(),
              earNotched: z.string().optional(),
              earNotchedDate: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODateTime.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              earNotchedPlacement: z.string().optional(),
              tagId: z.string().optional(),
              tagIdDate: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODateTime.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              tagIdPlacement: z.string().optional(),
              hotBrandedId: z.string().optional(),
              hotBrandedIdDate: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODateTime.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              hotBrandedIdPlacement: z.string().optional(),
              paintBranding: z.string().optional(),
              paintBrandingDate: z
                .string()
                .optional()
                .refine(
                  (val) => !val || regexRules.ISODateTime.test(val),
                  tCommonDateTimePicker('invalidFormat'),
                ),
              paintBrandingPlacement: z.string().optional(),
              homeDepartment: requiredOrOptional(
                Boolean(visibility.value.homeDepartment),
                PatientFieldName.HomeDepartment,
                tPatientDetails('fields.homeDepartment.validation.required'),
              ),
              ignoreDuplicates: z.string().optional(),
              remarks: z.string().optional(),
              tags: getTagsSchema(),
              address: z
                .object({
                  street_address: z.string().optional(),
                  street_address_2: z.string().optional(),
                  zip_code: z.string().optional(),
                  city: z.string().optional(),
                  state: z.string().optional(),
                  country: z.string().optional(),
                  country_region: z.string().optional(),
                })
                .optional(),
              ...customFieldsValidationSchema.value.shape,
            }),
            patientInsuranceFormZodSchema({
              insuranceNumber: requiredOrOptional(
                Boolean(visibility.value.insurance),
                PatientFieldName.Insurance,
                tCommonDateTimePicker('required'),
              ),
              invalidPolicyEndDateMessage:
                tCommonDateTimePicker('invalidFormat'),
              ifInsuranceNumberInvalidMessage: tPatientInsurance(
                'ifInsuranceNumberInvalid',
              ),
              requiredMessage: tCommonValidation('required'),
              isIfInsuranceVerificationFieldRequired,
              getIfInsuranceRegs: readIfInsuranceRegs,
            }),
          )
          .refine(
            (data) => {
              if (
                !visibility.value.farmStableName ||
                !checkRequirementWhenVisible(
                  visibility.value.farmStableName,
                  PatientFieldName.FarmStableName,
                )
              ) {
                return true
              }

              return (
                data.type === PatientTypes.SmallAnimal ||
                data.farmStableName?.trim()?.length
              )
            },
            {
              message: tPatientDetails(
                'fields.farmStableName.validation.required',
              ),
              path: ['farmStableName'],
            },
          )
          .refine(
            (data) => {
              const requireDateIfProvided = getRegulation?.(
                'require_microchip_date_if_provided',
              ) as boolean | undefined

              if (requireDateIfProvided && Boolean(data.microchip?.trim())) {
                return Boolean(data.microchipDate?.trim())
              }

              return true
            },
            {
              message: tCommonDateTimePicker('required'),
              path: ['microchipDate'],
            },
          )
          .refine(
            (data) =>
              patientFormPassesIfInsuranceVerification(
                data.insuranceCompany,
                data.insuranceNumber,
                (data as PatientDetailsFormSchema).customFields,
                readIfInsuranceRegs(),
              ),
            {
              message: tPatientInsurance('ifVerificationError'),
              path: [patientFormInsuranceNumberField],
            },
          ),
      ),
    ),
    initialValues: initialValues.value,
  })

  const onSubmit = async (
    event: Event,
    onSuccessCallback?: () => Promise<void>,
  ) => {
    event.preventDefault()

    await handleSubmit(
      // On validation success:
      async (data) => {
        // Success callback
        try {
          // Send data to the server
          if (onSuccessCallback) {
            await onSuccessCallback()
          }

          // After successful submission, reset the form with the new data
          // Make sure that missing fields get filled in object with `undefined` as value:
          for (const key in values) {
            if (!(key in data)) {
              ;(data as unknown as Record<string, unknown>)[key] = undefined
            }
          }

          // Update initial values with the new data
          initialValues.value = {
            ...initialValues.value,
            ...data,
          }
          await nextTick()
          resetForm({
            values: initialValues.value,
          })
        } catch (e) {
          console.error(e)
        }
      },
      // On validation error:
      () => handleErrors(),
    )()
  }

  const formValuesToModelAttributes = (): PatientCreate => {
    const {
      name,
      officialName,
      gender,
      dateOfBirth,
      dateOfRegistration,
      notForFood,
      bloodGroup,
      registerNumber,
      animalCode,
      microchip,
      microchipDate,
      passport,
      passportDate,
      color,
      additionalIdentification,
      cprStatus,
      farmStableName,
      dateOfDeath,
      reasonOfDeath,
      statuses,
      species,
      breed,
      customFields,
      isPrivate,
      remarks,
      address,
      insuranceCompany,
      insuranceNumber,
      insuranceExcessFeeType,
      insuranceExcessFeeAmount,
      insurancePolicyEndDate,
    } = values as PatientDetailsFormSchema

    const isDeceased = statuses?.includes('DECEASED') && dateOfDeath
    const addressCountry = address?.country
    const addressState = address?.state || address?.country_region || undefined
    const shouldUseState = addressCountry === 'US'
    const shouldUseCountryRegion =
      !addressCountry ||
      COUNTRY_REGION_FIELD_COUNTRIES.includes(
        addressCountry as CountryWithRegionField,
      )
    const normalizedState = fromISOStateCode(addressState)

    return {
      name,
      official_name: officialName || undefined,
      gender:
        gender ??
        (getInitialValue?.(PatientFieldName.Gender) as
          | PatientSex
          | undefined) ??
        'UNKNOWN',
      date_of_birth: dateOfBirth || undefined,
      date_of_registration: dateOfRegistration || undefined,
      blood_group: bloodGroup || undefined,
      register_number: registerNumber || undefined,
      animal_code: animalCode || undefined,
      microchip: microchip || undefined,
      microchip_date: microchipDate || undefined,
      passport_number: passport || undefined,
      passport_given: passportDate || undefined,
      color: color || undefined,
      additional_identification: additionalIdentification || undefined,
      archived: statuses?.includes('ARCHIVED') || false,
      not_for_food: notForFood ?? undefined,
      cpr_agreement: cprStatus || null, // Keep `null` as fallback value to avoid BE creating a redundant object & wrong
      stable_name: farmStableName || undefined,
      deceased: isDeceased ? dateOfDeath : undefined,
      reason_of_death: isDeceased && reasonOfDeath ? reasonOfDeath : undefined,
      species_id: Number(species),
      breed_id: breed ? Number(breed) : undefined,
      custom_fields: customFields || undefined,
      private: isPrivate ?? undefined,
      remarks: remarks || undefined,
      street_address: address?.street_address || undefined,
      street_address_2: address?.street_address_2 || undefined,
      zip_code: address?.zip_code || undefined,
      city: address?.city || undefined,
      ...(shouldUseState
        ? { state: (normalizedState || undefined) as PatientCreate['state'] }
        : {}),
      ...(shouldUseCountryRegion
        ? { country_region: addressState || undefined }
        : {}),
      insurance: insuranceNumber || undefined,
      insurance_company_id: insuranceCompany
        ? Number(insuranceCompany)
        : undefined,
      insurance_excess_fee_type: insuranceExcessFeeType || undefined,
      insurance_excess_fee_amount: insuranceExcessFeeAmount ?? undefined,
      insurance_policy_end_date: insurancePolicyEndDate || undefined,
    }
  }

  return {
    initialValues,
    values: values as PatientDetailsFormSchema,
    onSubmit,
    resetForm,
    resetField,
    setValues,
    setFieldValue,
    setErrors,
    errors,
    formValuesToModelAttributes,
    pairedCustomFields,
  }
}

export function usePatientDetailsFormFieldData() {
  const LOCALE_DATE_FORMAT = useCurrentLocaleDateFormat()
  const tPatientCPR = useTranslation('common.patientCPR')
  const tPatientPrivacyStatus = useTranslation('common.patientPrivacyStatus')
  const { translate: tEnumSex } = useEnumSexTranslation()
  const { departmentsListOptions, departmentGroupsListOptions } = useAuth()
  const { reasonOfDeathOptions, patientTypeOptions, getPatientSubtypeOptions } =
    usePatientDetailsFormFieldOptions()

  // PatientCPR Status
  const getPatientCPRAgreementStatus = (
    agreement?: PatientCPRStatus | undefined,
  ) => {
    const key = agreement || PatientCPRStatusValues.NotAsked
    let icon, iconColor, color, description

    const tooltips = {
      [PatientCPRStatusValues.CPR]: tPatientCPR('tooltipCPR'),
      [PatientCPRStatusValues.ACPR]: tPatientCPR('tooltipACPR'),
      [PatientCPRStatusValues.DNR]: tPatientCPR('tooltipDNR'),
      [PatientCPRStatusValues.NotAsked]: tPatientCPR('tooltipNotAsked'),
    }

    switch (key) {
      case PatientCPRStatusValues.CPR:
        description = tooltips[key]
        color = 'var(--n-color-status-success)'
        iconColor = 'var(--n-color-status-success)'
        icon = 'interface-cpr-acpr'
        break
      case PatientCPRStatusValues.ACPR:
        description = tooltips[key]
        color = 'var(--n-color-status-info)'
        iconColor = 'var(--n-color-status-info)'
        icon = 'interface-cpr-acpr'
        break
      case PatientCPRStatusValues.DNR:
        description = tooltips[key]
        color = 'var(--n-color-status-danger)'
        iconColor = 'var(--n-color-status-danger)'
        icon = 'interface-dnr'
        break
      case PatientCPRStatusValues.NotAsked:
      default:
        description = tPatientCPR('tooltipUnknownStatus')
        color = 'var(--n-color-text-weakest)'
        iconColor = 'var(--n-color-text-weakest)'
        icon = 'interface-cpr-unknown'
        break
    }

    return {
      key,
      description,
      tooltip: tooltips[key],
      color,
      iconColor,
      icon,
    }
  }

  // Patient Sex
  const getPatientSexIcon = (gender: PatientDetailsFormSchema['gender']) => {
    let icon = undefined
    switch (gender) {
      case 'FEMALE':
      case 'MARE':
        icon = 'medical-female'
        break
      case 'FEMALE_FIXED':
        icon = 'medical-spayed'
        break
      case 'GELDING':
      case 'MALE_FIXED':
        icon = 'medical-neutered'
        break
      case 'MALE':
      case 'STALLION':
        icon = 'medical-male'
        break
      case 'UNKNOWN':
      default:
        icon = undefined
    }

    return icon
  }
  const getPatientSex = (gender: PatientDetailsFormSchema['gender']) => ({
    label: gender ? tEnumSex(gender as PatientSex) : '',
    icon: gender ? getPatientSexIcon(gender || '') : undefined,
  })

  // Primary Clinic Location
  const getPatientPrimaryClinicLocation = (
    primaryClinicLocation: PatientDetailsFormSchema['primaryClinicLocation'],
  ) =>
    departmentsListOptions.find(
      (option) => option.value === primaryClinicLocation,
    )?.label

  // Patient Departments Visibility
  const getPatientDepartmentsVisibility = (
    visibleForDepartments: PatientDetailsFormSchema['visibleForDepartments'],
  ) =>
    visibleForDepartments?.map((departmentId: Option['value']) => {
      const departmentOption = departmentsListOptions?.find(
        (option) => option.value === departmentId,
      )

      return departmentOption?.label
    })

  // Patient Department Groups Visibility
  const getPatientDepartmentGroupsVisibility = (
    visibleForDepartmentGroups: PatientDetailsFormSchema['visibleForDepartmentGroups'],
  ) =>
    visibleForDepartmentGroups?.map((departmentGroupId: Option['value']) => {
      const departmentGroupOption = departmentGroupsListOptions?.find(
        (option) => option.value === departmentGroupId,
      )

      return departmentGroupOption?.label
    })

  // Patient Types and Subtypes
  const getPatientTypesAndSubtypes = (
    type: PatientDetailsFormSchema['type'],
    subtype: PatientDetailsFormSchema['subtype'] = [],
  ) => {
    const selectedPatientTypeLabel = patientTypeOptions.find(
      (optionType) => optionType.value === (type as PatientTypes),
    )?.label

    const selectedPatientSubTypesLabels = getPatientSubtypeOptions(type)
      ?.filter((optionSubtype) => subtype?.includes(optionSubtype.value))
      ?.map((optionSubtype) => optionSubtype.label)

    return [selectedPatientTypeLabel, ...selectedPatientSubTypesLabels].filter(
      Boolean,
    )
  }

  const getPatientPrivacyStatusBadge = (
    isPrivate: PatientDetailsFormSchema['isPrivate'],
  ) =>
    isPrivate
      ? tPatientPrivacyStatus('fields.privacyStatus.values.private')
      : tPatientPrivacyStatus('fields.privacyStatus.values.public')

  // Patient Reason of Death
  const getPatientReasonOfDeath = (
    reasonOfDeath: PatientDetailsFormSchema['reasonOfDeath'],
  ) => {
    const reasonOfDeathOption = reasonOfDeathOptions.find(
      (option) => option.value === reasonOfDeath,
    )

    return reasonOfDeathOption?.label
  }

  // Patient Date of Death
  const getPatientDateOfDeath = (
    dateOfDeath: PatientDetailsFormSchema['dateOfDeath'],
  ) => (dateOfDeath ? formatDate(dateOfDeath, LOCALE_DATE_FORMAT) : '')

  // Patient Species
  const speciesIcons = {
    Cat: 'generic-feline', // cat, lynx, leopard, ocelot
    Dog: 'generic-canine', // dog, fox, wolf, jackal, coyote
    Bird: 'generic-birds', // parakeets, lovebirds, canaries, parrots
    Rabbit: 'generic-leporidae', // rabbit, hare
    Horse: 'generic-equidae',
  } satisfies Record<PatientSpecies, string>
  const getPatientSpeciesIcon = (species: PatientSpecies) => {
    return (
      speciesIcons?.[species as keyof typeof speciesIcons] || 'generic-pet-paw'
    )
  }

  return {
    speciesIcons,
    getPatientSex,
    getPatientSexIcon,
    getPatientSpeciesIcon,
    getPatientCPRAgreementStatus,
    getPatientPrimaryClinicLocation,
    getPatientTypesAndSubtypes,
    getPatientPrivacyStatusBadge,
    getPatientDepartmentsVisibility,
    getPatientDepartmentGroupsVisibility,
    getPatientReasonOfDeath,
    getPatientDateOfDeath,
  }
}

/**
 * @param minimalVersion - When true, returns only Small Animal patient type option. When false, returns all patient type options including Farm Production, Equine, and Exotic/Other.
 */
export function usePatientDetailsFormFieldOptions(
  minimalVersion: boolean = false,
) {
  const tPatientType = useTranslation('common.patientType')

  // Species
  const speciesData = useSpecies({
    // After the species are fetched once, we don't need to have automatic refetching.
    // This is a workaround for pages where multiple patients are being displayed and
    // we don't want to refetch the species data on every single patient load.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  })
  const speciesOptions = computed(
    () =>
      speciesData.data?.data?.map((species) => ({
        label: species.attributes?.name || '',
        value: species.id as PatientSpecies,
        icon: species.attributes?.species_icon || '',
      })) || [],
  )

  // Blood Group
  const bloodGroupOptions = [
    { label: 'A', value: PatientBloodGroupValues.A },
    { label: 'B', value: PatientBloodGroupValues.B },
    { label: 'AB', value: PatientBloodGroupValues.AB },
    { label: 'C', value: PatientBloodGroupValues.C },
    { label: 'D', value: PatientBloodGroupValues.D },
    { label: 'K', value: PatientBloodGroupValues.K },
    { label: 'P', value: PatientBloodGroupValues.P },
    { label: 'Q', value: PatientBloodGroupValues.Q },
    { label: 'U', value: PatientBloodGroupValues.U },
    { label: 'T', value: PatientBloodGroupValues.T },
    { label: 'DEA 1.1 Positive', value: PatientBloodGroupValues.DEA11Positive },
    { label: 'DEA 1.1 Negative', value: PatientBloodGroupValues.DEA11Negative },
  ]

  // Farm/Stable name
  const farmStableNameOptions = [
    { label: 'Eagle Nest', value: 'EAGLE_NEST' },
    { label: 'Horse Haven', value: 'HORSE_HAVEN' },
  ]

  // Patient Types
  const minimalPatientTypeOptions = [
    {
      label: tPatientType('fields.type.options.smallAnimal'),
      value: PatientTypes.SmallAnimal,
    },
  ]
  const patientTypeOptions: {
    label: string
    value: PatientTypes
  }[] = minimalVersion
    ? minimalPatientTypeOptions
    : [
        ...minimalPatientTypeOptions,
        {
          label: tPatientType('fields.type.options.farmProduction'),
          value: PatientTypes.FarmProduction,
        },
        {
          label: tPatientType('fields.type.options.equine'),
          value: PatientTypes.Equine,
        },
        {
          label: tPatientType('fields.type.options.exoticOther'),
          value: PatientTypes.ExoticOther,
        },
      ]

  // Patient Subtypes
  const getPatientSubtypeOptions = (type: PatientDetailsFormSchema['type']) => [
    ...(type === PatientTypes.SmallAnimal
      ? [
          {
            label: tPatientType(
              'fields.subtype.options.notFoodProductionAnimal',
            ),
            value: PatientSubTypes.NotFoodProductionAnimal,
          },
        ]
      : []),
    {
      label: tPatientType('fields.subtype.options.zooAnimal'),
      value: PatientSubTypes.ZooAnimal,
    },
    {
      label: tPatientType('fields.subtype.options.labResearchAnimal'),
      value: PatientSubTypes.LabResearchAnimal,
    },
  ]

  // Reason of Death
  const reasonOfDeathOptions = [
    { label: 'Consumed too much lasagna', value: 'CONSUMED_TOO_MUCH_LASAGNA' },
    { label: 'Orange tabby', value: 'ORANGE_TABBY' },
  ]

  return {
    speciesOptions,
    bloodGroupOptions,
    farmStableNameOptions,
    reasonOfDeathOptions,
    patientTypeOptions,
    getPatientSubtypeOptions,
  }
}

/**
 * A composable that handles the logic for displaying the
 * additional identification field in the patient details form
 */
export function usePatientDetailsAdditionalIdentification(
  value: PatientDetailsFormSchema['additionalIdentification'],
  setFieldValue: FormContext<PatientDetailsFormSchema>['setFieldValue'],
) {
  const displayAdditionalIdentification = ref(Boolean(value))
  const removeAdditionalIdentification = () => {
    setFieldValue('additionalIdentification', '')
    displayAdditionalIdentification.value = false
  }
  watch(
    () => value,
    () => {
      if (value && !displayAdditionalIdentification.value) {
        displayAdditionalIdentification.value = true
      }
    },
  )

  return {
    displayAdditionalIdentification,
    removeAdditionalIdentification,
  }
}

/**
 * A composable that centralizes the shared logic and state
 * for patient identification form fields
 */
export function usePatientIdentification(patientType?: MaybeRef<PatientTypes>) {
  const tPatientIdentification = useTranslation('common.patientIdentification')

  // Get all the keys for the identification fields
  const identificationFieldsKeys = Object.values(PatientIdentificationFields)

  // Create a reactive object to store the form fields.
  // Each field is expected to have useField's `value` and `setValue` function
  const formIdentificationFields = ref(
    {} as Record<
      (typeof identificationFieldsKeys)[number],
      {
        value: PatientDetailsFormSchema[(typeof identificationFieldsKeys)[number]]
        setValue: (
          value: PatientDetailsFormSchema[(typeof identificationFieldsKeys)[number]],
        ) => void
      }
    >,
  )
  onBeforeMount(() => {
    // Get the values of the identification fields from the form
    identificationFieldsKeys.forEach((identificationFieldName) => {
      const { value, setValue } = useField<
        PatientDetailsFormSchema[typeof identificationFieldName]
      >(identificationFieldName)

      Object.assign(formIdentificationFields.value, {
        [identificationFieldName]: {
          value,
          setValue,
        },
      })
    })
  })

  // List of PatientTypes fields that contain values
  const formIdentificationFieldsWithValues = computed<
    (keyof PatientIdentificationValues | keyof PatientMicrochipValues)[]
  >(() =>
    identificationFieldsKeys.filter((key) =>
      Boolean(formIdentificationFields.value[key]?.value),
    ),
  )

  // Mapping of identification types to field metadata
  const fieldInfoMap: {
    [key in PatientIdentifications]: {
      hideLabel?: boolean
      label: string
      labelDate?: string
      labelInput?: string
      labelPlacement?: string
      name: keyof PatientIdentificationValues | keyof PatientMicrochipValues
      nameDate?:
        | keyof PatientIdentificationValues
        | keyof PatientMicrochipValues
      namePlacement?:
        | keyof PatientIdentificationValues
        | keyof PatientMicrochipValues
    }
  } = {
    [PatientIdentifications.Microchip]: {
      label: tPatientIdentification('fields.microchip.label'),
      labelInput: tPatientIdentification('fields.microchip.inputLabel'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.Microchip,
      nameDate: `${PatientIdentifications.Microchip}Date`,
      // namePlacement: `${PatientIdentifications.Microchip}Placement`, // @todo: add when API is ready
    },
    [PatientIdentifications.Passport]: {
      label: tPatientIdentification('fields.passport.label'),
      labelInput: tPatientIdentification('fields.passport.inputLabel'),
      labelDate: tPatientIdentification('issueDateLabel'),
      name: PatientIdentifications.Passport,
      nameDate: `${PatientIdentifications.Passport}Date`,
    },
    [PatientIdentifications.RegisterNumber]: {
      hideLabel: true,
      label: tPatientIdentification('fields.registerNumber.label'),
      name: PatientIdentifications.RegisterNumber,
    },
    [PatientIdentifications.AnimalCode]: {
      hideLabel: true,
      label: tPatientIdentification('fields.animalCode.label'),
      name: PatientIdentifications.AnimalCode,
    },
    [PatientIdentifications.TagId]: {
      label: tPatientIdentification('fields.tagId.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.TagId,
      nameDate: `${PatientIdentifications.TagId}Date`,
      namePlacement: `${PatientIdentifications.TagId}Placement`,
    },
    [PatientIdentifications.EarNotched]: {
      label: tPatientIdentification('fields.earNotched.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.EarNotched,
      nameDate: `${PatientIdentifications.EarNotched}Date`,
      namePlacement: `${PatientIdentifications.EarNotched}Placement`,
    },
    [PatientIdentifications.TattoedId]: {
      label: tPatientIdentification('fields.tattoedId.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.TattoedId,
      nameDate: `${PatientIdentifications.TattoedId}Date`,
      namePlacement: `${PatientIdentifications.TattoedId}Placement`,
    },
    [PatientIdentifications.FreezeMarking]: {
      label: tPatientIdentification('fields.freezeMarking.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.FreezeMarking,
      nameDate: `${PatientIdentifications.FreezeMarking}Date`,
      namePlacement: `${PatientIdentifications.FreezeMarking}Placement`,
    },
    [PatientIdentifications.HotBrandedId]: {
      label: tPatientIdentification('fields.hotBrandedId.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.HotBrandedId,
      nameDate: `${PatientIdentifications.HotBrandedId}Date`,
      namePlacement: `${PatientIdentifications.HotBrandedId}Placement`,
    },
    [PatientIdentifications.PaintBranding]: {
      label: tPatientIdentification('fields.paintBranding.label'),
      labelDate: tPatientIdentification('dateLabel'),
      labelPlacement: tPatientIdentification('placementLabel'),
      name: PatientIdentifications.PaintBranding,
      nameDate: `${PatientIdentifications.PaintBranding}Date`,
      namePlacement: `${PatientIdentifications.PaintBranding}Placement`,
    },
  }

  // Determines the default identification types based on the passed patient type
  const defaultIdentificationTypes = computed(() => {
    const defaultIdentificationTypesMap: Record<
      PatientTypes,
      PatientIdentifications[]
    > = {
      [PatientTypes.SmallAnimal]: [
        // PatientIdentifications.TagId, // @todo: add when API is ready
        PatientIdentifications.Passport,
        PatientIdentifications.RegisterNumber,
        PatientIdentifications.AnimalCode,
      ],
      [PatientTypes.FarmProduction]: [
        // PatientIdentifications.TagId, // @todo: add when API is ready
        PatientIdentifications.TattoedId,
        PatientIdentifications.RegisterNumber,
        PatientIdentifications.AnimalCode,
      ],
      [PatientTypes.Equine]: [
        // PatientIdentifications.TagId, // @todo: add when API is ready
        PatientIdentifications.Passport,
        PatientIdentifications.RegisterNumber,
        PatientIdentifications.AnimalCode,
      ],
      [PatientTypes.ExoticOther]: [
        // PatientIdentifications.TagId, // @todo: add when API is ready
        PatientIdentifications.TattoedId,
        PatientIdentifications.Passport,
        PatientIdentifications.RegisterNumber,
        PatientIdentifications.AnimalCode,
      ],
    }

    if (!patientType) {
      return []
    }
    return defaultIdentificationTypesMap[unref(patientType)] || []
  })

  // Determines the "other" identification types (those that are not in the default list)
  const otherIdentificationsTypes = computed(() => {
    const allTypes = Object.values(PatientIdentifications)
    return allTypes.filter(
      (type) => !defaultIdentificationTypes.value.includes(type),
    )
  })

  // Return field information by identification type
  const getFieldInfo = (type: PatientIdentifications) => fieldInfoMap[type]

  const isIdentificationFieldKey = (
    key: keyof PatientIdentificationValues | keyof PatientMicrochipValues,
  ): key is (typeof identificationFieldsKeys)[number] =>
    key in formIdentificationFields.value

  const clearIdentificationField = (
    key?: keyof PatientIdentificationValues | keyof PatientMicrochipValues,
  ) => {
    if (!key || !isIdentificationFieldKey(key)) {
      return
    }
    formIdentificationFields.value[key].setValue('')
  }

  // Given the form’s fields that have values, return any “other” types that are set
  const getOtherIdentificationTypesWithValues =
    (): PatientIdentifications[] => {
      const typesWithValues = formIdentificationFieldsWithValues.value.reduce<
        PatientIdentifications[]
      >((acc, key) => {
        // Determine the type by checking the start of the key (case-insensitive)
        const matchingType = Object.values(PatientIdentifications).find(
          (type) => key.toLowerCase().startsWith(type.toLowerCase()),
        )
        if (matchingType) {
          acc.push(matchingType)
        }
        return acc
      }, [])
      return _uniq(typesWithValues).filter(
        (type) => !defaultIdentificationTypes.value.includes(type),
      )
    }

  // State for the additional (“other”) identification types
  const selectedOtherTypes = ref<PatientIdentifications[]>([])
  onMounted(() => {
    selectedOtherTypes.value = getOtherIdentificationTypesWithValues()
  })

  // Get the list of available options for "other" fields
  const getOtherOptions = (fieldToSkip?: PatientIdentifications) => {
    const activeValues = new Set([
      ...defaultIdentificationTypes.value,
      ...selectedOtherTypes.value.filter((opt) => opt !== fieldToSkip),
    ])
    return otherIdentificationsTypes.value
      .filter((option) => !activeValues.has(option))
      .map((option) => ({
        label: fieldInfoMap[option].label,
        value: option,
      }))
  }

  // Whether adding an extra field is allowed
  const allowAddingOtherField = computed(() => getOtherOptions()?.length)

  // Add a new "other" field if an option is available
  const addOtherField = () => {
    const options = getOtherOptions()
    if (options.length) {
      selectedOtherTypes.value.push(options[0].value)
    }
  }

  // Remove field and clear its values
  const removeField = async (field: PatientIdentifications) => {
    const index = selectedOtherTypes.value.indexOf(field)
    if (index !== -1) {
      selectedOtherTypes.value.splice(index, 1)
    }
    await nextTick()
    const fieldInfo = getFieldInfo(field)
    const keys = [fieldInfo.name, fieldInfo.nameDate, fieldInfo.namePlacement]
    keys.forEach((key) => clearIdentificationField(key))
  }

  return {
    fieldInfoMap,
    defaultIdentificationTypes,
    otherIdentificationsTypes,
    formIdentificationFields,
    formIdentificationFieldsWithValues,
    selectedOtherTypes,
    allowAddingOtherField,
    getFieldInfo,
    getOtherOptions,
    isIdentificationFieldKey,
    removeField,
    addOtherField,
  }
}
